const DEFAULT_REDIRECT = "/dashboard";

function looksLikeOpenRedirect(path: string): boolean {
  if (!path.startsWith("/")) return true;
  if (path.startsWith("//")) return true;
  if (path.startsWith("/\\")) return true;
  // A backslash anywhere in the path is suspicious — browsers and some
  // routers normalize backslashes into forward slashes.
  if (path.includes("\\")) return true;
  return false;
}

/**
 * Whitelist-ish sanitizer for the `from` pathname coming off React Router
 * location state. Stops the classic "open redirect via login" vector —
 * anything that could resolve to an absolute URL, a protocol, a scheme-relative
 * path (`//evil.com`), a backslash-escaped variant (`/\evil.com`), or the
 * percent-encoded equivalents (`/%2f%2fevil.com`) is rejected and falls back
 * to the safe default.
 */
export function sanitizeRedirectPath(raw: unknown): string {
  if (typeof raw !== "string") return DEFAULT_REDIRECT;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return DEFAULT_REDIRECT;

  if (looksLikeOpenRedirect(trimmed)) return DEFAULT_REDIRECT;

  // Decode percent-encoding once (not recursively — repeated decoding loops
  // are their own attack surface) and re-run the structural checks. This
  // catches `/%2f%2fevil.com`, `/%5cevil`, and friends.
  let decoded: string;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch {
    // Malformed percent-encoding — reject rather than guess.
    return DEFAULT_REDIRECT;
  }
  if (decoded !== trimmed && looksLikeOpenRedirect(decoded)) return DEFAULT_REDIRECT;

  // Reject anything that parses as an absolute URL with a scheme (javascript:,
  // data:, http://, etc.). Path-only strings will throw and fall through.
  try {
    const maybeAbsolute = new URL(trimmed);
    if (maybeAbsolute.protocol) return DEFAULT_REDIRECT;
  } catch {
    // Not a valid absolute URL — which is exactly what we want for a path.
  }

  // Don't let login-loop itself be a target.
  if (trimmed === "/login" || trimmed.startsWith("/login?")) return DEFAULT_REDIRECT;

  return trimmed;
}

export { DEFAULT_REDIRECT };
